import { Box, Breadcrumb, Flex } from "@chakra-ui/react";
import React, { FC, ElementType, ReactNode, ReactElement } from "react";
import Menu from "./Menu";
import MenuToggle from "./MenuToggle";
import { NavigationItem, BreadcrumbItem } from "./types";

type Props = {
  logo: ReactElement;
  homeUrl: string;
  links: NavigationItem[];
  breadcrumbs?: BreadcrumbItem[];
  secondaryControls?: ReactElement[];
  feedbackLink: ReactElement;
  // allow consumer to use next/link for example
  linkComponent?: ElementType;
  children?: ReactNode;
};

const PageHeader: FC<Props> = ({
  logo,
  homeUrl,
  links = [],
  breadcrumbs = [],
  secondaryControls = [],
  feedbackLink,
  linkComponent: LinkComponent = "a",
  children,
}) => (
  <Box as="header" gridArea="header">
    {children}

    <Flex
      as="nav"
      h={100}
      py={4}
      color="#fff"
      fontWeight={700}
      backgroundColor="primary"
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={4}
        w="100%"
        maxWidth={1200}
        margin="0 auto"
        px={5}
      >
        <Flex flexShrink={0} align="center">
          {logo}
        </Flex>

        <MenuToggle>
          <Menu
            links={links}
            secondaryControls={secondaryControls}
            linkComponent={LinkComponent}
            isInDrawer
          />
        </MenuToggle>

        <Box display={["none", null, "block"]} mx="auto">
          <Menu
            links={links}
            secondaryControls={secondaryControls}
            linkComponent={LinkComponent}
          />
        </Box>
      </Flex>
    </Flex>

    <Box fontSize="0.875rem" bg="#f7fafc">
      <Flex
        flexWrap="nowrap"
        gap={4}
        alignItems="center"
        justifyContent="space-between"
        maxWidth={1200}
        w="100vw"
        margin="0 auto"
      >
        <Breadcrumb.Root
          gap={1}
          flex="1 1 auto"
          overflowX="auto"
          whiteSpace="nowrap"
          borderRight={["1px dashed rgba(0, 0, 0, .2)", null, "none"]}
          p={4}
        >
          <Breadcrumb.List>
            {[{ title: "Home", href: homeUrl } as BreadcrumbItem]
              .concat(breadcrumbs)
              .map((breadcrumb, index) => {
                const { title, href } = breadcrumb;
                const isLastItem = breadcrumbs.length === index;

                // last item is the "current" item
                const Wrapper = isLastItem
                  ? Breadcrumb.CurrentLink
                  : React.Fragment;

                return (
                  <React.Fragment key={index}>
                    <Breadcrumb.Item>
                      <Wrapper>
                        {href ? (
                          <Breadcrumb.Link
                            asChild
                            fontWeight={isLastItem ? "bold" : undefined}
                            textDecoration="underline"
                            wordBreak="break-word"
                          >
                            <LinkComponent href={href}>{title}</LinkComponent>
                          </Breadcrumb.Link>
                        ) : (
                          <Box as="span" wordBreak="break-word">
                            {title}
                          </Box>
                        )}
                      </Wrapper>
                    </Breadcrumb.Item>

                    {!isLastItem && <Breadcrumb.Separator />}
                  </React.Fragment>
                );
              })}
          </Breadcrumb.List>
        </Breadcrumb.Root>

        <Flex alignItems="center" gap={4} flex="0 0 auto" mr={3}>
          {[feedbackLink, ...secondaryControls].map((control, index) => (
            <Box key={index} display={{ base: "none", md: "flex" }}>
              {control}
            </Box>
          ))}

          <Box display={{ base: "block", md: "none" }}>{feedbackLink}</Box>
        </Flex>
      </Flex>
    </Box>
  </Box>
);

export default PageHeader;
